import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import { OrderStatusBadge } from './Dashboard';

/* ── Gem color swatches by category slug ── */
const GEM_GRADIENT = {
  sapphire:    'linear-gradient(135deg, #4a7fe8 0%, #1a3a8a 100%)',
  ruby:        'linear-gradient(135deg, #e84a6a 0%, #8a1a2a 100%)',
  alexandrite: 'linear-gradient(135deg, #9a5ae8 0%, #3a1a8a 100%)',
  emerald:     'linear-gradient(135deg, #4ae89a 0%, #1a6a3a 100%)',
  spinel:      'linear-gradient(135deg, #e84aa0 0%, #6a1a50 100%)',
  default:     'linear-gradient(135deg, #b8a080 0%, #6a5030 100%)',
};

/* ─────────────────────────────────────────────
   GEMS LIST
───────────────────────────────────────────── */
export function GemsAdmin() {
  const [gems,       setGems]   = useState([]);
  const [search,     setSearch] = useState('');
  const [filter,     setFilter] = useState('all');
  const [page,       setPage]   = useState(1);
  const [pagination, setPag]    = useState({});
  const [loading,    setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    const p = new URLSearchParams({ page, limit:15 });
    if (search) p.set('search', search);
    api.get(`/admin/gems?${p}`).then(r => { setGems(r.data.data||[]); setPag(r.data.pagination||{}); }).finally(()=>setLoading(false));
  };
  useEffect(() => { load(); }, [page, search]);

  const toggleActive = async (gem) => {
    await api.put(`/admin/gems/${gem.id}`, { ...gem, is_active: gem.is_active ? 0 : 1, images:[] });
    toast.success(`${gem.name} ${gem.is_active ? 'deactivated' : 'activated'}`);
    load();
  };
  const deleteGem = async (gem) => {
    if (!window.confirm(`Delete "${gem.name}"? This cannot be undone.`)) return;
    await api.delete(`/admin/gems/${gem.id}`);
    toast.success('Gem deleted'); load();
  };

  const FILTERS = ['all','active','featured','hidden'];
  const filtered = gems.filter(g => {
    if (filter === 'active')   return g.is_active && !g.is_featured;
    if (filter === 'featured') return g.is_featured;
    if (filter === 'hidden')   return !g.is_active;
    return true;
  });

  return (
    <div style={{ fontFamily:'var(--font-sans)' }}>
      {/* Header */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:28 }}>
        <div>
          <p style={{ fontSize:10.5, letterSpacing:'1.5px', textTransform:'uppercase', color:'var(--muted)', marginBottom:4, fontWeight:500 }}>Inventory</p>
          <h1 style={{ fontFamily:'var(--font-serif)', fontSize:34, fontWeight:400, letterSpacing:'-0.5px', color:'var(--white)', margin:0 }}>Gems Collection</h1>
          <p style={{ fontSize:13, color:'var(--muted)', marginTop:6 }}>{pagination.total||0} total gems</p>
        </div>
        <Link to="/admin/gems/new" className="btn btn-gold">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          Add New Gem
        </Link>
      </div>

      {/* Filter bar */}
      <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:'var(--radius)', padding:18, marginBottom:20, display:'flex', gap:14, alignItems:'center', flexWrap:'wrap', boxShadow:'var(--shadow-sm)' }}>
        <div style={{ position:'relative', flex:'1', minWidth:200 }}>
          <div style={{ position:'absolute', left:12, top:'50%', transform:'translateY(-50%)', color:'var(--muted)', pointerEvents:'none' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
          </div>
          <input value={search} onChange={e=>{setSearch(e.target.value);setPage(1);}}
            className="form-input" placeholder="Search by name or SKU…"
            style={{ paddingLeft:36 }} />
        </div>
        <div className="pill-tabs">
          {FILTERS.map(f => (
            <button key={f} onClick={()=>setFilter(f)} className={filter===f?'is-active':''} style={{ textTransform:'capitalize' }}>{f}</button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:'var(--radius)', overflow:'hidden', boxShadow:'var(--shadow-sm)' }}>
        <div className="table-wrap">
          <table>
            <thead>
              <tr><th>Gem</th><th className="col-mob-hide">SKU</th><th className="col-mob-hide">Category</th><th className="col-mob-hide">Carats</th><th>Price</th><th className="col-mob-hide">Stock</th><th>Status</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {loading ? [...Array(8)].map((_,i) => (
                <tr key={i}><td colSpan={8}><div className="skeleton" style={{ height:20, margin:'4px 0' }} /></td></tr>
              )) : filtered.map(gem => {
                const slug = gem.category_name?.toLowerCase() || 'default';
                const grad = GEM_GRADIENT[slug] || GEM_GRADIENT.default;
                return (
                  <tr key={gem.id}>
                    <td>
                      <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                        {gem.primary_image
                          ? <img src={gem.primary_image} alt="" style={{ width:48, height:48, objectFit:'cover', borderRadius:6, border:'1px solid var(--border)', flexShrink:0 }} />
                          : <div style={{ width:48, height:48, borderRadius:6, background:grad, flexShrink:0,
                              boxShadow:'inset 0 0 0 1px rgba(255,255,255,0.2), inset 2px 2px 8px rgba(255,255,255,0.15)' }} />
                        }
                        <div>
                          <p style={{ fontSize:13, color:'var(--white)', fontWeight:500 }}>{gem.name}</p>
                          {gem.tag && <span className="badge badge-gold" style={{ marginTop:3, fontSize:9 }}>{gem.tag}</span>}
                          {gem.is_featured && <span className="badge badge-info" style={{ marginTop:3, marginLeft:4, fontSize:9 }}>Featured</span>}
                        </div>
                      </div>
                    </td>
                    <td className="col-mob-hide" style={{ color:'var(--muted)', fontSize:11.5, fontFamily:'monospace' }}>{gem.sku}</td>
                    <td className="col-mob-hide" style={{ color:'var(--muted)', fontSize:13 }}>{gem.category_name}</td>
                    <td className="col-mob-hide">
                      <span style={{ fontFamily:'var(--font-serif)', fontSize:14, color:'var(--white)' }}>{gem.weight_ct || '—'}</span>
                      {gem.weight_ct && <span style={{ fontSize:11, color:'var(--muted)', marginLeft:3 }}>ct</span>}
                    </td>
                    <td style={{ color:'var(--gold)', fontWeight:500 }}>${Number(gem.price_usd).toLocaleString()}</td>
                    <td className="col-mob-hide">
                      <span style={{
                        fontWeight:500,
                        color: gem.stock_qty===0 ? 'var(--danger)' : gem.stock_qty<=2 ? 'var(--warning)' : 'var(--success)',
                        fontSize:13,
                      }}>{gem.stock_qty}</span>
                      <span style={{ fontSize:10, color:'var(--muted)', marginLeft:4 }}>
                        {gem.stock_qty===0?'out':gem.stock_qty<=2?'low':''}
                      </span>
                    </td>
                    <td><span className={`badge ${gem.is_active?'badge-success':'badge-muted'}`}>{gem.is_active?'Active':'Hidden'}</span></td>
                    <td>
                      <div style={{ display:'flex', gap:6 }}>
                        <Link to={`/admin/gems/${gem.id}`} className="btn btn-outline btn-sm">Edit</Link>
                        <button onClick={()=>toggleActive(gem)} className="btn btn-sm" style={{
                          border:`1px solid ${gem.is_active?'rgba(168,106,24,0.4)':'rgba(31,122,77,0.4)'}`,
                          color: gem.is_active?'var(--warning)':'var(--success)', padding:'6px 12px',
                          borderRadius:6, fontSize:12,
                        }}>{gem.is_active?'Hide':'Show'}</button>
                        <button onClick={()=>deleteGem(gem)} className="btn btn-sm" style={{
                          border:'1px solid rgba(176,58,46,0.35)', color:'var(--danger)',
                          padding:'6px 12px', borderRadius:6, fontSize:12,
                        }}>Del</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {!loading && !filtered.length && (
                <tr><td colSpan={8} style={{ textAlign:'center', color:'var(--muted)', padding:'48px 0' }}>No gems found</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {pagination.pages > 1 && (
        <div className="pagination">
          <button className="page-btn" disabled={page<=1} onClick={()=>setPage(p=>p-1)}>‹</button>
          {[...Array(pagination.pages)].map((_,i)=>(
            <button key={i} className={`page-btn${i+1===page?' active':''}`} onClick={()=>setPage(i+1)}>{i+1}</button>
          ))}
          <button className="page-btn" disabled={page>=pagination.pages} onClick={()=>setPage(p=>p+1)}>›</button>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   GEM FORM
───────────────────────────────────────────── */
const EMPTY = { name:'', category_id:'', short_desc:'', description:'', price_usd:'', compare_price:'', weight_ct:'', dimensions:'', color:'', clarity:'', cut:'', treatment:'None / Unheated', origin:'Sri Lanka', mine_location:'Ratnapura, Sri Lanka', certification:'', cert_number:'', stock_qty:1, is_featured:false, is_active:true, tag:'', images:[] };

export function GemForm() {
  const { id }     = useParams();
  const navigate   = useNavigate();
  const isEdit     = Boolean(id) && id !== 'new';
  const [form,      setForm]     = useState(EMPTY);
  const [cats,      setCats]     = useState([]);
  const [loading,   setLoading]  = useState(false);
  const [saving,    setSaving]   = useState(false);
  const [uploading, setUploading]= useState(false);
  const [dragOver,  setDragOver] = useState(false);
  const [imgUrl,    setImgUrl]   = useState('');
  const fileRef = useRef(null);

  useEffect(() => {
    api.get('/categories').then(r => setCats(r.data.data||[]));
    if (isEdit) {
      setLoading(true);
      api.get(`/admin/gems/${id}`).then(r => {
        const g = r.data.data;
        if (g) {
          const imgs = Array.isArray(g.images) ? g.images.filter(Boolean).map(i => ({ url: i.url, alt: i.alt })) : [];
          setForm({ ...EMPTY, ...g, is_featured: Boolean(g.is_featured), is_active: Boolean(g.is_active), images: imgs });
        }
      }).catch(() => toast.error('Failed to load gem'))
        .finally(() => setLoading(false));
    }
  }, [id, isEdit]);

  const f = k => e => setForm({...form, [k]: e.target.type==='checkbox'?e.target.checked:e.target.value});

  const handleFiles = async (files) => {
    const slots = 4 - form.images.length;
    if (slots <= 0) return toast.error('Maximum 4 images per gem');
    const toUpload = Array.from(files).slice(0, slots);
    setUploading(true);
    try {
      const fd = new FormData();
      toUpload.forEach(file => fd.append('images', file));
      const res = await api.post('/admin/upload/gem', fd, { timeout: 60000 });
      const newImgs = res.data.urls.map(url => ({ url, alt: form.name || 'Gem' }));
      setForm(prev => ({ ...prev, images: [...prev.images, ...newImgs] }));
      toast.success(`${newImgs.length} image${newImgs.length > 1 ? 's' : ''} uploaded`);
    } catch { toast.error('Upload failed — check file size and type'); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const addImage = () => {
    if (!imgUrl.trim()) return;
    if (form.images.length >= 4) return toast.error('Maximum 4 images per gem');
    setForm({...form, images:[...form.images, {url:imgUrl.trim(), alt:form.name}]});
    setImgUrl('');
  };
  const removeImage = i => setForm({...form, images:form.images.filter((_,idx)=>idx!==i)});
  const setPrimary  = i => setForm({...form, images:[form.images[i], ...form.images.filter((_,idx)=>idx!==i)]});

  const handleSubmit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const payload = { ...form, price_usd:parseFloat(form.price_usd), stock_qty:parseInt(form.stock_qty)||0,
        compare_price:form.compare_price?parseFloat(form.compare_price):null,
        weight_ct:form.weight_ct?parseFloat(form.weight_ct):null };
      if (isEdit) await api.put(`/admin/gems/${id}`, payload);
      else        await api.post('/admin/gems', payload);
      toast.success(isEdit ? 'Gem updated!' : 'Gem created!');
      navigate('/admin/gems');
    } catch (err) { toast.error(err.response?.data?.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  const Toggle = ({ field, label }) => (
    <label style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16, cursor:'pointer' }}>
      <span style={{ fontSize:13, color:'var(--white)', fontFamily:'var(--font-sans)' }}>{label}</span>
      <div onClick={()=>setForm({...form,[field]:!form[field]})} style={{
        width:36, height:20, borderRadius:999, position:'relative', cursor:'pointer',
        background: form[field] ? '#0c0c10' : 'var(--border2)', transition:'background 0.2s ease',
        boxShadow: form[field] ? 'inset 0 0 0 1px rgba(201,169,110,0.5)' : 'none',
      }}>
        <div style={{
          position:'absolute', top:2, left: form[field] ? 18 : 2,
          width:16, height:16, borderRadius:'50%', background:'#fff',
          transition:'left 0.2s ease', boxShadow:'0 1px 3px rgba(0,0,0,0.2)',
        }} />
      </div>
    </label>
  );

  if (loading) return <div style={{ display:'flex', justifyContent:'center', padding:80 }}><div className="spinner" style={{ width:36, height:36 }} /></div>;

  return (
    <div style={{ fontFamily:'var(--font-sans)' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:28 }}>
        <div>
          <p style={{ fontSize:10.5, letterSpacing:'1.5px', textTransform:'uppercase', color:'var(--muted)', marginBottom:4, fontWeight:500 }}>Inventory</p>
          <h1 style={{ fontFamily:'var(--font-serif)', fontSize:34, fontWeight:400, letterSpacing:'-0.5px', color:'var(--white)', margin:0 }}>
            {isEdit ? 'Edit Gem' : 'Add New Gem'}
          </h1>
        </div>
        <div style={{ display:'flex', gap:10 }}>
          <Link to="/admin/gems" className="btn btn-outline">← Back</Link>
          <button form="gem-form" type="submit" disabled={saving} className="btn btn-gold">
            {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Gem'}
          </button>
        </div>
      </div>

      <form id="gem-form" onSubmit={handleSubmit}>
        <div className="admin-form-grid" style={{ display:'grid', gridTemplateColumns:'1fr 340px', gap:24, alignItems:'start' }}>
          <div style={{ display:'flex', flexDirection:'column', gap:20 }}>

            {/* Basic info */}
            <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:'var(--radius)', padding:28, boxShadow:'var(--shadow-sm)' }}>
              <h3 style={{ fontFamily:'var(--font-serif)', fontSize:18, fontWeight:500, color:'var(--white)', marginBottom:22 }}>Basic Information</h3>
              <div className="form-group"><label className="form-label">Gem Name *</label>
                <input required value={form.name} onChange={f('name')} className="form-input" placeholder="e.g. Blue Ceylon Sapphire" />
              </div>
              <div className="grid-2">
                <div className="form-group"><label className="form-label">Category *</label>
                  <select required value={form.category_id} onChange={f('category_id')} className="form-select">
                    <option value="">Select category</option>
                    {cats.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div className="form-group"><label className="form-label">Tag / Badge</label>
                  <input value={form.tag} onChange={f('tag')} className="form-input" placeholder="Rare, New, Best Seller…" />
                </div>
              </div>
              <div className="form-group"><label className="form-label">Short Description</label>
                <input value={form.short_desc} onChange={f('short_desc')} className="form-input" placeholder="Max 500 chars" maxLength={500} />
              </div>
              <div className="form-group"><label className="form-label">Full Description *</label>
                <textarea required value={form.description} onChange={f('description')} className="form-input form-textarea" style={{ minHeight:130 }} placeholder="Detailed description…" />
              </div>
            </div>

            {/* Gem specs */}
            <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:'var(--radius)', padding:28, boxShadow:'var(--shadow-sm)' }}>
              <h3 style={{ fontFamily:'var(--font-serif)', fontSize:18, fontWeight:500, color:'var(--white)', marginBottom:22 }}>Gemological Specifications</h3>
              <div className="grid-2">
                {[['weight_ct','Weight (carats)','3.20'],['color','Color','Cornflower Blue'],['clarity','Clarity','VVS'],['cut','Cut','Oval'],['treatment','Treatment','None / Unheated'],['dimensions','Dimensions','9.2 × 7.8 × 5.1 mm']].map(([k,l,ph])=>(
                  <div key={k} className="form-group"><label className="form-label">{l}</label>
                    <input value={form[k]} onChange={f(k)} className="form-input" placeholder={ph} />
                  </div>
                ))}
              </div>
              <div className="grid-2">
                {[['origin','Origin','Sri Lanka'],['mine_location','Mine Location','Ratnapura, Sri Lanka'],['certification','Certificate (GIA/AGL)','GIA'],['cert_number','Certificate Number','']].map(([k,l,ph])=>(
                  <div key={k} className="form-group"><label className="form-label">{l}</label>
                    <input value={form[k]} onChange={f(k)} className="form-input" placeholder={ph} />
                  </div>
                ))}
              </div>
            </div>

            {/* Images */}
            <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:'var(--radius)', padding:28, boxShadow:'var(--shadow-sm)' }}>
              <h3 style={{ fontFamily:'var(--font-serif)', fontSize:18, fontWeight:500, color:'var(--white)', marginBottom:4 }}>Product Images</h3>
              <p style={{ fontSize:12, color:'var(--muted)', marginBottom:18 }}>Up to 4 images. First image is the primary listing photo. Click any image to set it as primary.</p>

              {/* Drop zone — hidden when 4 images already added */}
              {form.images.length < 4 && (
                <div
                  onClick={() => fileRef.current?.click()}
                  onDrop={e => { e.preventDefault(); setDragOver(false); handleFiles(e.dataTransfer.files); }}
                  onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  style={{
                    border: `2px dashed ${dragOver ? 'var(--gold)' : 'var(--border2)'}`,
                    borderRadius: 10, padding: '28px 20px', textAlign: 'center',
                    cursor: uploading ? 'default' : 'pointer',
                    background: dragOver ? 'rgba(201,169,110,0.06)' : 'var(--surface2)',
                    transition: 'all 0.2s', marginBottom: 16,
                  }}
                >
                  <input ref={fileRef} type="file" multiple accept="image/jpeg,image/png,image/webp,image/gif"
                    style={{ display:'none' }} onChange={e => handleFiles(e.target.files)} />
                  {uploading ? (
                    <>
                      <div className="spinner" style={{ margin:'0 auto 10px' }} />
                      <p style={{ fontSize:13, color:'var(--muted)' }}>Uploading…</p>
                    </>
                  ) : (
                    <>
                      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="var(--gold)" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" style={{ margin:'0 auto 10px', display:'block', opacity:0.7 }}>
                        <rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/>
                        <polyline points="21 15 16 10 5 21"/>
                      </svg>
                      <p style={{ fontSize:13, fontWeight:500, color:'var(--white)', marginBottom:4 }}>Click to upload or drag & drop</p>
                      <p style={{ fontSize:11, color:'var(--muted)' }}>JPG, PNG, WebP · Max 10 MB each · Up to {4 - form.images.length} more</p>
                    </>
                  )}
                </div>
              )}

              {/* URL fallback */}
              <div style={{ display:'flex', gap:8, marginBottom: form.images.length > 0 ? 16 : 0 }}>
                <input value={imgUrl} onChange={e=>setImgUrl(e.target.value)} className="form-input"
                  placeholder="Or paste an external image URL…"
                  onKeyDown={e=>e.key==='Enter'&&(e.preventDefault(),addImage())}
                  style={{ fontSize:12 }} />
                <button type="button" onClick={addImage} className="btn btn-outline" style={{ flexShrink:0, fontSize:12 }}>Add URL</button>
              </div>

              {/* Image grid */}
              {form.images.length > 0 && (
                <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:10 }}>
                  {form.images.map((img, i) => (
                    <div key={i} title={i===0 ? 'Primary image' : 'Click to set as primary'}
                      onClick={() => i !== 0 && setPrimary(i)}
                      style={{ position:'relative', aspectRatio:'1', borderRadius:8, overflow:'hidden', border:`2px solid ${i===0?'var(--gold)':'var(--border2)'}`, cursor: i===0 ? 'default' : 'pointer', transition:'border-color 0.2s' }}>
                      <img src={img.url}
                        alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }}
                        onError={e=>{ e.target.style.opacity=0.2; }} />
                      {i===0 && (
                        <div style={{ position:'absolute', bottom:0, left:0, right:0, background:'var(--gold)', color:'#0c0c10', fontSize:8, textAlign:'center', padding:'3px 0', letterSpacing:1, fontWeight:700 }}>
                          PRIMARY
                        </div>
                      )}
                      <button type="button" onClick={e=>{e.stopPropagation();removeImage(i);}}
                        style={{ position:'absolute', top:4, right:4, width:22, height:22, borderRadius:'50%', background:'rgba(176,58,46,0.9)', color:'#fff', fontSize:11, display:'flex', alignItems:'center', justifyContent:'center', border:'none', cursor:'pointer' }}>
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Right rail */}
          <div style={{ display:'flex', flexDirection:'column', gap:18, position:'sticky', top:80 }}>
            {/* Pricing */}
            <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:'var(--radius)', padding:22, boxShadow:'var(--shadow-sm)' }}>
              <h3 style={{ fontFamily:'var(--font-serif)', fontSize:16, fontWeight:500, color:'var(--white)', marginBottom:18 }}>Pricing & Stock</h3>
              <div className="form-group"><label className="form-label">Price (USD) *</label>
                <input required type="number" min="0" step="0.01" value={form.price_usd} onChange={f('price_usd')} className="form-input" placeholder="1500.00" />
              </div>
              <div className="form-group"><label className="form-label">Compare Price (USD)</label>
                <input type="number" min="0" step="0.01" value={form.compare_price} onChange={f('compare_price')} className="form-input" placeholder="Original price" />
              </div>
              <div className="form-group"><label className="form-label">Stock Quantity</label>
                <input type="number" min="0" value={form.stock_qty} onChange={f('stock_qty')} className="form-input" />
              </div>
            </div>

            {/* Visibility */}
            <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:'var(--radius)', padding:22, boxShadow:'var(--shadow-sm)' }}>
              <h3 style={{ fontFamily:'var(--font-serif)', fontSize:16, fontWeight:500, color:'var(--white)', marginBottom:18 }}>Visibility</h3>
              <Toggle field="is_active"   label="Active (visible on site)" />
              <Toggle field="is_featured" label="Featured on homepage" />
            </div>

            <button type="submit" disabled={saving} className="btn btn-gold" style={{ width:'100%', justifyContent:'center', padding:'13px 24px' }}>
              {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Gem'}
            </button>
            {isEdit && (
              <button type="button" className="btn btn-sm" onClick={()=>deleteGem({id,name:'this gem'})}
                style={{ width:'100%', justifyContent:'center', border:'1px solid rgba(176,58,46,0.3)', color:'var(--danger)', padding:'10px 16px', borderRadius:8 }}>
                Delete Gem
              </button>
            )}
          </div>
        </div>
      </form>
    </div>
  );
}

/* ─────────────────────────────────────────────
   ORDERS LIST
───────────────────────────────────────────── */
export function OrdersAdmin() {
  const [orders,     setOrders]  = useState([]);
  const [stats,      setStats]   = useState({});
  const [filters,    setFilters] = useState({ status:'', search:'', page:1 });
  const [pagination, setPag]     = useState({});
  const [loading,    setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    const p = new URLSearchParams({ page:filters.page, limit:15 });
    if (filters.status) p.set('status', filters.status);
    if (filters.search) p.set('search', filters.search);
    api.get(`/admin/orders?${p}`).then(r => { setOrders(r.data.data||[]); setPag(r.data.pagination||{}); setStats(r.data.stats||{}); }).finally(()=>setLoading(false));
  };
  useEffect(() => { load(); }, [filters.status, filters.search, filters.page]);

  const STATUSES = ['','pending','confirmed','processing','shipped','delivered','cancelled'];

  return (
    <div style={{ fontFamily:'var(--font-sans)' }}>
      <div style={{ marginBottom:28 }}>
        <p style={{ fontSize:10.5, letterSpacing:'1.5px', textTransform:'uppercase', color:'var(--muted)', marginBottom:4, fontWeight:500 }}>Commerce</p>
        <h1 style={{ fontFamily:'var(--font-serif)', fontSize:34, fontWeight:400, letterSpacing:'-0.5px', color:'var(--white)', margin:0 }}>Orders</h1>
        <p style={{ fontSize:13, color:'var(--muted)', marginTop:6 }}>{pagination.total||0} total · ${Number(stats.total_revenue||0).toLocaleString()} USD</p>
      </div>

      <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:'var(--radius)', padding:18, marginBottom:20, display:'flex', gap:14, flexWrap:'wrap', alignItems:'center', boxShadow:'var(--shadow-sm)' }}>
        <div style={{ position:'relative', flex:'1', minWidth:200 }}>
          <div style={{ position:'absolute', left:12, top:'50%', transform:'translateY(-50%)', color:'var(--muted)', pointerEvents:'none' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
          </div>
          <input value={filters.search} onChange={e=>setFilters({...filters,search:e.target.value,page:1})}
            className="form-input" placeholder="Order # or customer…" style={{ paddingLeft:36 }} />
        </div>
        <div className="pill-tabs">
          {STATUSES.map(s => (
            <button key={s} onClick={()=>setFilters({...filters,status:s,page:1})} className={filters.status===s?'is-active':''} style={{ textTransform:'capitalize' }}>{s||'All'}</button>
          ))}
        </div>
      </div>

      <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:'var(--radius)', overflow:'hidden', boxShadow:'var(--shadow-sm)' }}>
        <div className="table-wrap">
          <table>
            <thead><tr><th>Order #</th><th>Customer</th><th className="col-mob-hide">Country</th><th>Total</th><th className="col-mob-hide">Payment</th><th>Status</th><th className="col-mob-hide">Date</th><th></th></tr></thead>
            <tbody>
              {loading ? [...Array(6)].map((_,i)=><tr key={i}><td colSpan={8}><div className="skeleton" style={{height:20,margin:'4px 0'}}/></td></tr>)
              : orders.map(o => (
                <tr key={o.id}>
                  <td style={{ fontFamily:'var(--font-serif)', fontSize:14, color:'var(--gold)' }}>{o.order_number}</td>
                  <td>
                    <p style={{ fontSize:13, color:'var(--white)', fontWeight:500 }}>{o.customer_name}</p>
                    <p style={{ fontSize:11, color:'var(--muted)' }}>{o.customer_email}</p>
                  </td>
                  <td className="col-mob-hide" style={{ color:'var(--muted)', fontSize:13 }}>{o.ship_country||'—'}</td>
                  <td style={{ color:'var(--white)', fontWeight:500 }}>${Number(o.total_usd).toLocaleString()}</td>
                  <td className="col-mob-hide"><span className={`badge ${o.payment_status==='paid'?'badge-success':'badge-warning'}`}>{o.payment_status}</span></td>
                  <td><OrderStatusBadge status={o.status}/></td>
                  <td className="col-mob-hide" style={{ color:'var(--muted)', fontSize:12 }}>{new Date(o.created_at).toLocaleDateString()}</td>
                  <td><Link to={`/admin/orders/${o.id}`} style={{ fontSize:12, color:'var(--gold)' }}>View →</Link></td>
                </tr>
              ))}
              {!loading&&!orders.length&&<tr><td colSpan={8} style={{textAlign:'center',color:'var(--muted)',padding:'48px 0'}}>No orders found</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {pagination.pages>1 && (
        <div className="pagination">
          <button className="page-btn" disabled={filters.page<=1} onClick={()=>setFilters({...filters,page:filters.page-1})}>‹</button>
          {[...Array(pagination.pages)].map((_,i)=><button key={i} className={`page-btn${i+1===filters.page?' active':''}`} onClick={()=>setFilters({...filters,page:i+1})}>{i+1}</button>)}
          <button className="page-btn" disabled={filters.page>=pagination.pages} onClick={()=>setFilters({...filters,page:filters.page+1})}>›</button>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   ORDER DETAIL
───────────────────────────────────────────── */
export function OrderDetail() {
  const { id }  = useParams();
  const [order, setOrder]  = useState(null);
  const [form,  setForm]   = useState({});
  const [saving,setSaving] = useState(false);

  useEffect(() => {
    api.get(`/admin/orders/${id}`).then(r => {
      const o = r.data.data;
      setOrder(o);
      setForm({ status:o.status, payment_status:o.payment_status, tracking_number:o.tracking_number||'', carrier:o.carrier||'', admin_notes:o.admin_notes||'' });
    });
  }, [id]);

  const save = async () => {
    setSaving(true);
    await api.put(`/admin/orders/${id}`, form);
    toast.success('Order updated');
    setSaving(false);
  };

  const STATUS_STEPS = ['confirmed','processing','shipped','delivered'];

  if (!order) return <div style={{display:'flex',justifyContent:'center',padding:80}}><div className="spinner" style={{width:36,height:36}}/></div>;

  const stepIdx = STATUS_STEPS.indexOf(order.status);

  return (
    <div style={{ fontFamily:'var(--font-sans)' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:28 }}>
        <div>
          <p style={{ fontSize:10.5, letterSpacing:'1.5px', textTransform:'uppercase', color:'var(--muted)', marginBottom:4, fontWeight:500 }}>Commerce</p>
          <h1 style={{ fontFamily:'Inter', fontWeight:300, fontSize:34, letterSpacing:'-0.5px', color:'var(--white)', margin:0 }}>
            {order.order_number}
          </h1>
          <p style={{ fontSize:13, color:'var(--muted)', marginTop:6 }}>{order.customer_name} · {new Date(order.created_at).toLocaleString()}</p>
        </div>
        <Link to="/admin/orders" className="btn btn-outline">← Back</Link>
      </div>

      <div className="admin-form-grid" style={{ display:'grid', gridTemplateColumns:'1fr 300px', gap:24 }}>
        <div style={{ display:'flex', flexDirection:'column', gap:20 }}>

          {/* Status timeline */}
          <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:'var(--radius)', padding:24, boxShadow:'var(--shadow-sm)' }}>
            <h3 style={{ fontFamily:'var(--font-serif)', fontSize:16, fontWeight:500, color:'var(--white)', marginBottom:20 }}>Order Status</h3>
            <div style={{ display:'flex', alignItems:'center', gap:0 }}>
              {STATUS_STEPS.map((s, i) => (
                <div key={s} style={{ display:'flex', alignItems:'center', flex:1 }}>
                  <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:6 }}>
                    <div style={{
                      width:28, height:28, borderRadius:'50%',
                      display:'flex', alignItems:'center', justifyContent:'center',
                      background: i < stepIdx ? '#0c0c10' : i === stepIdx ? 'transparent' : 'transparent',
                      border: i < stepIdx ? 'none' : i === stepIdx ? '2px solid var(--gold)' : '2px dashed var(--border2)',
                      color: i <= stepIdx ? 'var(--gold)' : 'var(--muted)',
                    }}>
                      {i < stepIdx
                        ? <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#c9a96e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                        : <div style={{ width:8, height:8, borderRadius:'50%', background: i === stepIdx ? 'var(--gold)' : 'var(--border2)' }} />
                      }
                    </div>
                    <span style={{ fontSize:10, color: i <= stepIdx ? 'var(--white)' : 'var(--muted)', textTransform:'capitalize', letterSpacing:'0.5px', fontWeight: i === stepIdx ? 500 : 400 }}>{s}</span>
                  </div>
                  {i < STATUS_STEPS.length-1 && (
                    <div style={{ flex:1, height:1, background: i < stepIdx ? 'var(--gold-dark)' : 'var(--border2)', margin:'0 4px', marginBottom:20 }} />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Customer */}
          <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:'var(--radius)', padding:24, boxShadow:'var(--shadow-sm)' }}>
            <h3 style={{ fontFamily:'var(--font-serif)', fontSize:16, fontWeight:500, color:'var(--white)', marginBottom:16 }}>Customer</h3>
            <p style={{ fontSize:14, color:'var(--white)', fontWeight:500 }}>{order.customer_name}</p>
            <p style={{ fontSize:13, color:'var(--muted)', marginTop:4, display:'flex', alignItems:'center', gap:6 }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
              {order.customer_email}
            </p>
            {order.customer_phone && <p style={{ fontSize:13, color:'var(--muted)', marginTop:4 }}>{order.customer_phone}</p>}
          </div>

          {/* Shipping */}
          <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:'var(--radius)', padding:24, boxShadow:'var(--shadow-sm)' }}>
            <h3 style={{ fontFamily:'var(--font-serif)', fontSize:16, fontWeight:500, color:'var(--white)', marginBottom:16 }}>Shipping Address</h3>
            <p style={{ fontSize:13, color:'var(--white)', lineHeight:1.8 }}>
              {order.ship_address}<br/>{order.ship_city}<br/>{order.ship_country} {order.ship_postal}
            </p>
          </div>

          {/* Items */}
          <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:'var(--radius)', padding:24, boxShadow:'var(--shadow-sm)' }}>
            <h3 style={{ fontFamily:'var(--font-serif)', fontSize:16, fontWeight:500, color:'var(--white)', marginBottom:16 }}>Order Items</h3>
            {order.items?.map((item, i) => (
              <div key={i} style={{ display:'flex', gap:14, padding:'12px 0', borderBottom:'1px solid var(--border)', alignItems:'center' }}>
                <div style={{ width:60, height:60, borderRadius:8, background:GEM_GRADIENT.default, flexShrink:0 }} />
                <div style={{ flex:1 }}>
                  <p style={{ fontSize:13, color:'var(--white)', fontWeight:500 }}>{item.gem_name}</p>
                  <p style={{ fontSize:11, color:'var(--muted)', marginTop:2 }}>SKU: {item.gem_sku} · Qty: {item.quantity}</p>
                </div>
                <p style={{ fontSize:14, color:'var(--gold)', fontWeight:500 }}>${Number(item.total_price).toLocaleString()}</p>
              </div>
            ))}
            <div style={{ marginTop:16, display:'flex', flexDirection:'column', gap:8 }}>
              {[['Subtotal', `$${Number(order.subtotal_usd).toLocaleString()}`],['Shipping',`$${Number(order.shipping_usd).toLocaleString()}`]].map(([k,v])=>(
                <div key={k} style={{ display:'flex', justifyContent:'space-between' }}>
                  <span style={{ fontSize:13, color:'var(--muted)' }}>{k}</span>
                  <span style={{ fontSize:13, color:'var(--white)' }}>{v}</span>
                </div>
              ))}
              <div style={{ display:'flex', justifyContent:'space-between', paddingTop:10, borderTop:'1px solid var(--border2)', marginTop:4 }}>
                <span style={{ fontFamily:'var(--font-serif)', fontSize:16, color:'var(--white)' }}>Total</span>
                <div>
                  <span style={{ fontFamily:'var(--font-serif)', fontSize:26, color:'var(--white)', fontWeight:400 }}>${Number(order.total_usd).toLocaleString()}</span>
                  <span style={{ fontSize:12, color:'var(--muted)', marginLeft:6 }}>USD</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Rail */}
        <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
          <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:'var(--radius)', padding:22, boxShadow:'var(--shadow-sm)' }}>
            <h3 style={{ fontFamily:'var(--font-serif)', fontSize:16, fontWeight:500, color:'var(--white)', marginBottom:18 }}>Manage Order</h3>
            <div className="form-group"><label className="form-label">Order Status</label>
              <select value={form.status} onChange={e=>setForm({...form,status:e.target.value})} className="form-select">
                {['pending','confirmed','processing','shipped','delivered','cancelled','refunded'].map(s=><option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="form-group"><label className="form-label">Payment Status</label>
              <select value={form.payment_status} onChange={e=>setForm({...form,payment_status:e.target.value})} className="form-select">
                {['unpaid','paid','partially_refunded','refunded'].map(s=><option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="form-group"><label className="form-label">Carrier</label>
              <input value={form.carrier} onChange={e=>setForm({...form,carrier:e.target.value})} className="form-input" placeholder="DHL, FedEx, EMS…" />
            </div>
            <div className="form-group"><label className="form-label">Tracking Number</label>
              <input value={form.tracking_number} onChange={e=>setForm({...form,tracking_number:e.target.value})} className="form-input" placeholder="Tracking #" />
            </div>
            <div className="form-group"><label className="form-label">Internal Notes</label>
              <textarea value={form.admin_notes} onChange={e=>setForm({...form,admin_notes:e.target.value})} className="form-input" rows={3} placeholder="Internal notes…" />
            </div>
            <button onClick={save} disabled={saving} className="btn btn-gold" style={{ width:'100%', justifyContent:'center' }}>
              {saving?'Saving…':'Save Changes'}
            </button>
          </div>

          <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:'var(--radius)', padding:18, boxShadow:'var(--shadow-sm)' }}>
            <p style={{ fontSize:10.5, letterSpacing:'1.5px', textTransform:'uppercase', color:'var(--muted)', marginBottom:8, fontWeight:500 }}>Stripe Payment ID</p>
            <p style={{ fontSize:11, color:'var(--white)', wordBreak:'break-all', fontFamily:'monospace' }}>{order.stripe_payment_id||'—'}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default GemsAdmin;
